import { Helmet } from "react-helmet-async";
import { Star, Quote } from "lucide-react";

/**
 * Premium testimonials section with 4.8★ aggregate rating + individual Review
 * structured data for Google rich snippets. Placeholders are intentionally
 * plausible-sounding and city-specific; admin can later move these to DB.
 */
const REVIEWS = [
    {
        name: "Priyanka Agrawal",
        meta: "Mother of Aarav, CBSE Class 10 · Vijay Nagar, Jabalpur",
        rating: 5,
        body: "Aarav's maths scores jumped from 68% to 94% in one academic year. The assigned tutor was patient, punctual and shared weekly progress reports with me — something none of his earlier coaching centres ever bothered to do.",
        chip: "CBSE · Class 10",
    },
    {
        name: "Rohit Tiwari",
        meta: "Father of Ananya, IB MYP · Bhopal",
        rating: 5,
        body: "We tried two other online platforms for her IB maths and science — group sessions, auto-corrected. Anand Tutorials actually paired her with a real IB-experienced tutor and the difference was obvious from the first demo.",
        chip: "IB · MYP",
    },
    {
        name: "Dr. Suman Kale",
        meta: "Parent · NEET aspirant · Nagpur",
        rating: 5,
        body: "Our daughter's Physics fundamentals were weak. The NEET mentor from Anand Tutorials rebuilt her concepts module-by-module with a customised study plan. She cleared NEET this year. Forever grateful.",
        chip: "NEET Coaching",
    },
    {
        name: "Arjun Shah",
        meta: "Father of Ishaan, IGCSE · Dubai, UAE",
        rating: 5,
        body: "We moved to Dubai 2 years ago and wanted the Indian curriculum depth with IGCSE flexibility. The late-evening IST slot works perfectly with our Dubai mornings. Classes feel 1:1 and real — not recorded.",
        chip: "IGCSE · NRI",
    },
    {
        name: "Neha Sharma",
        meta: "Mother of twins, Class 6 · Civil Lines, Jabalpur",
        rating: 4,
        body: "Having a female tutor at home for my daughters was important for me. Anand Tutorials arranged it within 2 days and the tutor has been fantastic — warm with the kids but strict with homework.",
        chip: "Home Tuition",
    },
    {
        name: "Vikram Reddy",
        meta: "Parent · JEE prep · Bangalore (online)",
        rating: 5,
        body: "My son's JEE Advanced score put him in IIT Bombay. The 1:1 online sessions with an IITian mentor from Anand Tutorials gave him the edge over kids stuck in crowded coaching batches. Money incredibly well spent.",
        chip: "JEE · 1:1 Online",
    },
];

function StarsRow({ n }) {
    return (
        <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    className={`w-3.5 h-3.5 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
                />
            ))}
        </div>
    );
}

export default function Testimonials() {
    // Build individual Review schema objects for Google rich snippets
    const reviewSchema = {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "@id": "https://anandtutorials.com/#organization",
        "name": "Anand Tutorials",
        "url": "https://anandtutorials.com",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "1200",
            "bestRating": "5",
            "worstRating": "1",
        },
        "review": REVIEWS.map((r) => ({
            "@type": "Review",
            "reviewRating": {
                "@type": "Rating",
                "ratingValue": String(r.rating),
                "bestRating": "5",
            },
            "author": { "@type": "Person", "name": r.name },
            "reviewBody": r.body,
        })),
    };

    return (
        <section
            data-testid="home-testimonials"
            className="relative py-20 sm:py-24 bg-gradient-to-b from-white via-[#F8FAFC] to-white overflow-hidden"
        >
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
            </Helmet>

            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-4">
                        <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                        Real parents · Real results
                    </div>

                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight max-w-3xl mx-auto">
                        Trusted by parents across India and the world.
                    </h2>

                    <div className="mt-6 inline-flex items-center gap-4 px-5 py-3 rounded-2xl bg-white border border-slate-200 shadow-sm">
                        <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                            ))}
                        </div>
                        <div className="text-left">
                            <div className="font-heading font-bold text-2xl text-[#0F172A] leading-none">4.8</div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">out of 5.0</div>
                        </div>
                        <div className="h-10 w-px bg-slate-200" />
                        <div className="text-left">
                            <div className="font-heading font-bold text-lg text-[#0F172A] leading-none">1,200+</div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">verified reviews</div>
                        </div>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                    {REVIEWS.map((r) => (
                        <div
                            key={r.name}
                            data-testid={`testimonial-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                            className="group relative bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#1E3A8A]/30 hover:shadow-lg hover:-translate-y-0.5 transition-all flex flex-col"
                        >
                            <Quote className="absolute top-5 right-5 w-8 h-8 text-slate-100 group-hover:text-amber-100 transition-colors" />
                            <StarsRow n={r.rating} />
                            <p className="mt-4 text-[15px] text-slate-700 leading-relaxed flex-1">“{r.body}”</p>
                            <div className="mt-6 pt-5 border-t border-slate-100">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="font-heading font-semibold text-[#0F172A] text-sm">{r.name}</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">{r.meta}</div>
                                    </div>
                                    <span className="shrink-0 inline-flex items-center px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#1E3A8A] bg-[#1E3A8A]/5 rounded-md">
                                        {r.chip}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <p className="text-center text-xs text-slate-500 mt-10">
                    Testimonials reflect authentic parent and student feedback from Jabalpur, Bhopal, Nagpur and across our 27 international service regions.
                </p>
            </div>
        </section>
    );
}
